import { FocusMonitor, A11yModule } from '@angular/cdk/a11y';
import * as i0 from '@angular/core';
import { signal, computed, Injectable, inject, ElementRef, input, numberAttribute, Directive, booleanAttribute, Component, ChangeDetectionStrategy, HostListener, NgModule } from '@angular/core';
const _c0 = ["uxa-skip-link", ""];
const _c1 = ["*"];
class UxaSkipLinkManager {
  constructor() {
    /** The element to skip to. */
    this._target = signal(null);
    /** Determine if there is a target to skip to. */
    this.hasTarget = computed(() => this._target() !== null);
  }
  /** The element to skip to. */
  registerTarget(target) {
    this._target.set(target);
  }
  /** Unset the target when the target is destroyed. */
  unregisterTarget(target) {
    if (this._target() === target) {
      this._target.set(null);
    }
  }
  /** Focus the target element. */
  focusTarget() {
    this._target()?.focus();
  }
  static {
    this.ɵfac = function UxaSkipLinkManager_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaSkipLinkManager)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: UxaSkipLinkManager,
      factory: UxaSkipLinkManager.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaSkipLinkManager, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
class UxaSkipLinkTargetDirective {
  constructor() {
    /** Access the element. */
    this._element = inject(ElementRef);
    /** Access the skip link service. */
    this._skipLink = inject(UxaSkipLinkManager);
    /** The tabindex of the element. */
    this.tabindex = input(-1, {
      transform: numberAttribute
    });
  }
  ngOnInit() {
    this._skipLink.registerTarget(this._element.nativeElement);
  }
  ngOnDestroy() {
    this._skipLink.unregisterTarget(this._element.nativeElement);
  }
  static {
    this.ɵfac = function UxaSkipLinkTargetDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaSkipLinkTargetDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaSkipLinkTargetDirective,
      selectors: [["", "uxaSkipLinkTarget", ""]],
      hostVars: 1,
      hostBindings: function UxaSkipLinkTargetDirective_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("tabindex", ctx.tabindex);
        }
      },
      inputs: {
        tabindex: [1, "tabindex"]
      }
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaSkipLinkTargetDirective, [{
    type: Directive,
    args: [{
      standalone: true,
      selector: '[uxaSkipLinkTarget]',
      host: {
        '[attr.tabindex]': 'tabindex'
      }
    }]
  }], null, null);
})();

/**
 * Service for managing focus of elements
 */
class UxaFocusIndicator {
  constructor() {
    this._focusMonitor = inject(FocusMonitor);
  }
  /** Track focus of a element and add/remove the class based on focus origin */
  monitor(element, checkChildren = false) {
    this._focusMonitor.monitor(element, checkChildren).subscribe(origin => {
      if (origin === 'keyboard') {
        element.classList.add('uxa-focused');
      } else {
        element.classList.remove('uxa-focused');
      }
    });
  }
  /** Stop tracking focus for an element */
  stopMonitoring(element) {
    this._focusMonitor.stopMonitoring(element);
  }
  /** Programmatically trigger focus on an element with a specified origin */
  focusVia(element, origin, options) {
    this._focusMonitor.focusVia(element, origin, options);
  }
  static {
    this.ɵfac = function UxaFocusIndicator_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaFocusIndicator)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: UxaFocusIndicator,
      factory: UxaFocusIndicator.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaFocusIndicator, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();

/**
 * The `uxa-focus-indicator` directive can be used to apply focus to a component.
 */
class UxaFocusIndicatorDirective {
  constructor() {
    this._elementRef = inject(ElementRef);
    this._focusIndicator = inject(UxaFocusIndicator);
  }
  ngOnInit() {
    const checkChildren = this._elementRef.nativeElement.hasAttribute('uxaFocusWithinIndicator') || this._elementRef.nativeElement.hasAttribute('uxa-focus-indicator');
    this._focusIndicator.monitor(this._elementRef.nativeElement, checkChildren);
  }
  ngOnDestroy() {
    this._focusIndicator.stopMonitoring(this._elementRef.nativeElement);
  }
  /** Programmatically trigger focus of host element with a specified origin. */
  focusVia(origin, element, options) {
    this._focusIndicator.focusVia(element ?? this._elementRef.nativeElement, origin, options);
  }
  static {
    this.ɵfac = function UxaFocusIndicatorDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaFocusIndicatorDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaFocusIndicatorDirective,
      selectors: [["", "uxaFocusIndicator", ""], ["", "uxaFocusWithinIndicator", ""]],
      exportAs: ["uxa-focus-indicator"]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaFocusIndicatorDirective, [{
    type: Directive,
    args: [{
      selector: '[uxaFocusIndicator],[uxaFocusWithinIndicator]',
      exportAs: 'uxa-focus-indicator',
      standalone: true
    }]
  }], null, null);
})();
class UxaSkipLinkComponent {
  constructor() {
    /** Access the skip link service. */
    this._skipLink = inject(UxaSkipLinkManager);
    /** Whether the skip link is disabled. */
    this.disabled = input(booleanAttribute(false), {
      transform: booleanAttribute
    });
    /** The href to navigate to. */
    this.href = input();
    /** We have a target to skip to. */
    this.hasTarget = computed(() => this._skipLink.hasTarget() || this.href() !== undefined);
  }
  /** Focus the target element when the skip link is clicked. */
  focusTarget() {
    // if there is an href then just let the browser handle the navigation
    if (this.href() || this.disabled()) {
      return;
    }
    this._skipLink.focusTarget();
  }
  static {
    this.ɵfac = function UxaSkipLinkComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaSkipLinkComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: UxaSkipLinkComponent,
      selectors: [["a", "uxa-skip-link", ""]],
      hostVars: 3,
      hostBindings: function UxaSkipLinkComponent_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("click", function UxaSkipLinkComponent_click_HostBindingHandler() {
            return ctx.focusTarget();
          })("keydown.enter", function UxaSkipLinkComponent_keydown_enter_HostBindingHandler() {
            return ctx.focusTarget();
          })("keydown.space", function UxaSkipLinkComponent_keydown_space_HostBindingHandler() {
            return ctx.focusTarget();
          });
        }
        if (rf & 2) {
          i0.ɵɵhostProperty("hidden", !ctx.hasTarget);
          i0.ɵɵattribute("tabindex", ctx.disabled() ? -1 : 0)("href", ctx.href(), i0.ɵɵsanitizeUrl);
        }
      },
      inputs: {
        disabled: [1, "disabled"],
        href: [1, "href"]
      },
      features: [i0.ɵɵHostDirectivesFeature([UxaFocusIndicatorDirective])],
      attrs: _c0,
      ngContentSelectors: _c1,
      decls: 1,
      vars: 0,
      template: function UxaSkipLinkComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵprojection(0);
        }
      },
      styles: ["[_nghost-%COMP%], .uxa-skip-link[_ngcontent-%COMP%]{display:inline-flex;position:fixed;inset-inline-start:var(--uxa-skip-link__inset-inline-start);inset-block-start:var(--uxa-skip-link__inset-block-start);padding-inline:var(--uxa-skip-link__padding-inline);padding-block:var(--uxa-skip-link__padding-block);background:var(--uxa-skip-link__background);border:var(--uxa-skip-link__border);box-shadow:var(--uxa-skip-link__box-shadow);color:var(--uxa-skip-link__color);border-radius:var(--uxa-skip-link__border-radius);text-decoration:var(--uxa-skip-link__text-decoration);box-sizing:border-box;font-weight:var(--uxa-skip-link__font-weight);font-size:var(--uxa-skip-link__font-size);line-height:var(--uxa-skip-link__line-height);cursor:var(--uxa-skip-link__cursor);z-index:var(--uxa-skip-link__z-index);transform:var(--uxa-skip-link__transform);opacity:var(--uxa-skip-link__opacity);transition:var(--uxa-skip-link__transition)}[_nghost-%COMP%]:hover, .uxa-skip-link[_ngcontent-%COMP%]:hover{background:var(--uxa-skip-link__background--hover);box-shadow:var(--uxa-skip-link__box-shadow--hover)}[_nghost-%COMP%]:active, .uxa-skip-link[_ngcontent-%COMP%]:active{background:var(--uxa-skip-link__background--active);box-shadow:var(--uxa-skip-link__box-shadow--active)}[_nghost-%COMP%]:focus, .uxa-skip-link[_ngcontent-%COMP%]:focus{outline:none}[_nghost-%COMP%]:not(.uxa-focused), .uxa-skip-link[_ngcontent-%COMP%]:not(.uxa-focused){border:0;clip:rect(0 0 0 0);height:1px;margin-inline:-1px;margin-block:-1px;overflow:hidden;padding-inline:0;padding-block:0;position:absolute;width:1px;white-space:nowrap;outline:0;-webkit-appearance:none;-moz-appearance:none;left:0;opacity:var(--uxa-skip-link__opacity--unfocused);transform:var(--uxa-skip-link__transform--unfocused)}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaSkipLinkComponent, [{
    type: Component,
    args: [{
      standalone: true,
      selector: 'a[uxa-skip-link]',
      changeDetection: ChangeDetectionStrategy.OnPush,
      hostDirectives: [UxaFocusIndicatorDirective],
      host: {
        '[attr.tabindex]': 'disabled() ? -1 : 0',
        '[attr.href]': 'href()',
        '[hidden]': '!hasTarget'
      },
      template: "<ng-content />\n",
      styles: [":host,.uxa-skip-link{display:inline-flex;position:fixed;inset-inline-start:var(--uxa-skip-link__inset-inline-start);inset-block-start:var(--uxa-skip-link__inset-block-start);padding-inline:var(--uxa-skip-link__padding-inline);padding-block:var(--uxa-skip-link__padding-block);background:var(--uxa-skip-link__background);border:var(--uxa-skip-link__border);box-shadow:var(--uxa-skip-link__box-shadow);color:var(--uxa-skip-link__color);border-radius:var(--uxa-skip-link__border-radius);text-decoration:var(--uxa-skip-link__text-decoration);box-sizing:border-box;font-weight:var(--uxa-skip-link__font-weight);font-size:var(--uxa-skip-link__font-size);line-height:var(--uxa-skip-link__line-height);cursor:var(--uxa-skip-link__cursor);z-index:var(--uxa-skip-link__z-index);transform:var(--uxa-skip-link__transform);opacity:var(--uxa-skip-link__opacity);transition:var(--uxa-skip-link__transition)}:host:hover,.uxa-skip-link:hover{background:var(--uxa-skip-link__background--hover);box-shadow:var(--uxa-skip-link__box-shadow--hover)}:host:active,.uxa-skip-link:active{background:var(--uxa-skip-link__background--active);box-shadow:var(--uxa-skip-link__box-shadow--active)}:host:focus,.uxa-skip-link:focus{outline:none}:host:not(.uxa-focused),.uxa-skip-link:not(.uxa-focused){border:0;clip:rect(0 0 0 0);height:1px;margin-inline:-1px;margin-block:-1px;overflow:hidden;padding-inline:0;padding-block:0;position:absolute;width:1px;white-space:nowrap;outline:0;-webkit-appearance:none;-moz-appearance:none;left:0;opacity:var(--uxa-skip-link__opacity--unfocused);transform:var(--uxa-skip-link__transform--unfocused)}\n"]
    }]
  }], null, {
    focusTarget: [{
      type: HostListener,
      args: ['click']
    }, {
      type: HostListener,
      args: ['keydown.enter']
    }, {
      type: HostListener,
      args: ['keydown.space']
    }]
  });
})();
class UxaFocusWhenDirective {
  constructor() {
    this._elementRef = inject(ElementRef);
    /** Whether to apply focus to the host element. */
    this.condition = input.required({
      alias: 'uxaFocusWhen'
    });
  }
  ngOnChanges() {
    if (this.condition()) {
      this._elementRef.nativeElement.focus();
    }
  }
  static {
    this.ɵfac = function UxaFocusWhenDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaFocusWhenDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaFocusWhenDirective,
      selectors: [["", "uxaFocusWhen", ""]],
      inputs: {
        condition: [1, "uxaFocusWhen", "condition"]
      },
      features: [i0.ɵɵNgOnChangesFeature]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaFocusWhenDirective, [{
    type: Directive,
    args: [{
      selector: '[uxaFocusWhen]',
      standalone: true
    }]
  }], null, null);
})();
class UxaA11yModule {
  static {
    this.ɵfac = function UxaA11yModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaA11yModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: UxaA11yModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [A11yModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaA11yModule, [{
    type: NgModule,
    args: [{
      imports: [A11yModule, UxaFocusIndicatorDirective, UxaFocusWhenDirective, UxaSkipLinkComponent, UxaSkipLinkTargetDirective],
      exports: [UxaFocusIndicatorDirective, UxaFocusWhenDirective, UxaSkipLinkComponent, UxaSkipLinkTargetDirective]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { UxaA11yModule, UxaFocusIndicator, UxaFocusIndicatorDirective, UxaFocusWhenDirective, UxaSkipLinkComponent, UxaSkipLinkTargetDirective };
